import React, { useEffect, useRef } from "react";
import { useDemoDrawer } from "../DemoDrawerContext";

/**
 * Deep-link opener for the in-page demo-scheduling drawer.
 *
 * Callers outside the marketing site (the Playground's "Fale connosco"
 * banner CTA, transactional emails, etc.) want to land a visitor directly
 * on the Calendly booking flow rather than the bare landing page they then
 * have to scroll and click through themselves. `/fidava?demo=1` opens the
 * drawer WITHOUT navigating anywhere — the route stays `/fidava`, the
 * drawer just opens on top. Same pattern as LegalDeepLink's `?legal=`.
 *
 * Renders nothing; it only reacts to the query param once on mount.
 */
const readDemoParam = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  return params.has("demo");
};

const DemoDeepLink: React.FC = () => {
  const { open } = useDemoDrawer();
  // Open at most once per load. Closing the drawer must not re-trigger it
  // (the param stays in the URL so the link remains shareable).
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) {
      return;
    }
    if (!readDemoParam()) {
      return;
    }
    handledRef.current = true;
    open();
  }, [open]);

  return null;
};

export default DemoDeepLink;
